/**
 * A head traced from a reference painted over the slender head (ไข่หน้าเรียว) and sampled
 * onto the figure's canvas grid (two pixels per art pixel), its outer outline left off (the finish
 * draws its own) and stray or speckled pixels from the picture's compression cleaned.
 *
 * The face is taken apart, as Model 0's face is: `REF_HEAD` is the bare head (skull, ears, skin
 * where the features were), and `REF_FACE` holds the features as separate parts — eyes, brows, nose
 * and mouth exactly as painted, plus the eyes shut (their lash lines) for blinks and meditation —
 * each placed back where it was. Each sprite starts at `x`, `y` canvas pixels from the head joint's
 * art pixel; letters index its palette.
 * Generated data: re-trace rather than edit by hand.
 */
import type { View } from '../sprites';

export interface FineSprite {
  x: number;
  y: number;
  palette: readonly string[];
  rows: readonly string[];
}

/** The parts of a traced face (a view shows those it has). */
export interface FineFace {
  eyes?: FineSprite;
  /** The eyes shut: drawn instead of `eyes` for a blink or closed eyes. */
  closed?: FineSprite;
  brows?: FineSprite;
  nose?: FineSprite;
  mouth?: FineSprite;
}

export const REF_HEAD: Partial<Record<View, FineSprite>> = {
  front: {
    x: -17,
    y: -18,
    palette: [
      '#6e665a',
      '#605851',
      '#8a6d5e',
      '#987663',
      '#ab8771',
      '#c59d81',
      '#d5aa89',
      '#e6b993',
      '#eec59c',
      '#544b48',
      '#f3d2ad',
      '#bd9274',
      '#2f1c16',
      '#3a2720',
      '#6b503e',
      '#624536',
      '#775947',
      '#3e2c32',
      '#edae86',
      '#d27850',
      '#a6705a',
      '#d98861',
      '#eba376',
      '#4b342c',
      '#201311',
      '#4e3f3e',
      '#938987',
      '#a69f9e',
      '#d4bfb2',
      '#efe9e6',
      '#f5f3f2',
      '#e0cab6',
      '#b8775e',
      '#eddfd4',
      '#774830',
      '#f6dbbc',
      '#8c644d',
      '#d89c71',
      '#ab634b',
      '#e9e3df',
    ],
    rows: [
      '....................cc.............',
      '..........defgghhhhhiigfe..........',
      '.........ggggghiiiiiiiiiik.........',
      '.......eggggghiiiiiiiiiiiik........',
      '......dgggggghiiiiiiiiiiiiiie......',
      '.....dgggggghiiiiiiiiiiiiiiiid.....',
      '.....ggggggghiiiiiiiiiiiiiiiii.....',
      '....eggggggghiiiiiiiiiiiiiiiiik....',
      '....gggggggghiiiiiiiiiiiiiiiiik....',
      '...fggggggghiiiiiiiiiiiiiiiiiiii...',
      '...gggggggghiiiiiiiiiiiiiiiiiiii...',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggghiiiiiiiiiiiiiiiiiiiif..',
      '..ggggggggggggiiiiiiiiiiiiiihiiif..',
      '..lgggggggggggiiiiiiiiiiiiiiiiiil..',
      '..eggggggghhhhiiiiiiiiiiiiiiiiiid..',
      'dsdgggggghhhhhhiiiiiiiiiiiiiiiiisl.',
      'stuggggggggggggiiiiiiiggggggiiihvsq',
      'wvtlggggggggggghiiiiiiiiiiiiiiilvwq',
      'wgvdggggggggggghiiiiiiiiiiiiiiiuwsq',
      'wgvdggggggggggggiiiiiiiiiiiigihtwso',
      '.wvGfghhhhhhhhgiiiiiiiiiiiiihievsu.',
      '..gllggggggggiiiiiiiiiiiiiikiiusl..',
      '...lugggkkkkkiiiiiiiihhhhhhiiguL...',
      '.....fggshhhiiiiiiiiiiiihhhiid.....',
      '.....dggwsssiiiiiiiiiiiiiihhi......',
      '......fgggggiiiiiiiiiiiiiiiil......',
      '......oggghhhiiiiiiiiiiiiiil.......',
      '........gghhhiiggggiiiiiiif........',
      '........qggggiihhhhiiiiiig.........',
      '..........ggghiiiiiiiiiiG..........',
      '...........lggiiiiiiiih............',
      '............qfgiiiiihu.............',
      '............IKcfhhglqK.............',
      '.............Mducucdlc.............',
      '............oGdeeeeefe.............',
      '............Kvvfllfghf.............',
      '............uLLiiiiihg.............',
      '............gLLiiiiiii.............',
    ],
  },
  front3: {
    x: -17,
    y: -18,
    palette: [
      '#6e6559',
      '#5c544d',
      '#876b5b',
      '#a17d67',
      '#d6ab89',
      '#e2b691',
      '#ecc098',
      '#bc9f83',
      '#f4d2a9',
      '#b48b71',
      '#2e1d15',
      '#422c22',
      '#c99579',
      '#c58567',
      '#7d5f4c',
      '#725644',
      '#674d3d',
      '#bd6e51',
      '#e1a076',
      '#d48f6a',
      '#cc7c5c',
      '#1e1210',
      '#897f7e',
      '#a9a29f',
      '#a2938d',
      '#90796d',
      '#eee6e1',
      '#e9dacf',
      '#e0c3ac',
      '#beaca2',
      '#5f3a29',
      '#f5f3f1',
      '#bfb7b1',
      '#7e4e35',
      '#a86c56',
      '#e9e3df',
    ],
    rows: [
      '..........bb..........bb.........',
      '..........ddeefffggggehh.........',
      '........heeeeggggggggggggh.......',
      '.......eeeeeggggggggggggggi......',
      '.....ceeeeeeggggggggggggggii.....',
      '....jeeeeeefggggggggggggggggi....',
      '...ceeeeeeeggggggggggggggggggg...',
      '...eeeeeeeeggggggggggggggggggie..',
      '..heeeeeeeeggggggggggggggggggie..',
      '.beeeeeeeeegggggggggggggggggggi..',
      '.eeeeeeeeeeggggggggggggggggggggd.',
      'beeeeeeeeefggggggggggggggggggggib',
      'eeeeeeeeeefggggggggggggggggggggib',
      'eeeeeeeeeefggggggggggggggggggggib',
      'eeeeeeeeeeeggggggggggggggggggggib',
      'eeeeeeeeeeeggggggggggggggggggggib',
      'eeeeeeeeeeeggggggggggggggggggggib',
      'eeeeeeeeeeegggggggggggggggggggggb',
      'eeeeeeeeeegggggggggegggggggggggeb',
      'mejneeeeeegggggggggggggggggggggmb',
      'qnfnreeeefgggggggggggggggggggggib',
      'cssftteeefgggggggggggggggggggggm.',
      'snnmtteeeggggggggggggggggggdddgd.',
      'sttuteeeegghggggggggggggggggggg..',
      'sttusgeeeggogggggggggggggggggg...',
      'teuufgeeeggfggggggggggggggggggg..',
      'qftrrgeeegggggggggggggggggggggg..',
      'bpsnueeeeeggggggggggggggggggggd..',
      '.bcffeeeeegggggggggggggggggggg...',
      '....cIeeeegggggggggggggggggggfo..',
      '.....qeeeeeggggggggggggggegggeb..',
      '.....boeeeeggggggggggggggggggj...',
      '......boeeefggggggggggggggggi....',
      '........neeeggggggggggggggggo....',
      '.........rmeeggggggggggggggo.....',
      '.........snjeeggggggggggggcb.....',
      '.........ttnIjegggggggggge.......',
      '.........tttjdIdmfggggggj........',
      '.........tssjdjdccIchhhc.........',
      '.........ssssjjjjjq..............',
      '.........ssseejjjj...............',
      '.........sssggghmm...............',
      '.........sssggggee...............',
      '.........sssgggggi...............',
    ],
  },
  side: {
    x: -17,
    y: -18,
    palette: [
      '#5c544f',
      '#a6816d',
      '#b79677',
      '#bda084',
      '#544a44',
      '#d6ab87',
      '#ebbe93',
      '#eec9a0',
      '#f4d6b1',
      '#7f625d',
      '#676156',
      '#8f6e5f',
      '#312018',
      '#422d22',
      '#77574d',
      '#ac725e',
      '#cf7c58',
      '#b0674e',
      '#e8af82',
      '#db9069',
      '#6a443a',
      '#e3a077',
      '#4e362b',
      '#201412',
      '#887c78',
      '#ada4a2',
      '#ebe2db',
      '#815043',
      '#7a4b32',
      '#c58f71',
      '#9d6855',
      '#ce9a79',
      '#ca8763',
      '#e9e3df',
    ],
    rows: [
      '.............bbbbccdddd..........',
      '..........dffffffggggggggg.......',
      '........bfffffffggggggggghi......',
      '.......ffffffffggggggghhhhhgf....',
      '.....bfffffffffgggggghhghhhhhi...',
      '.....fffffffffggggggghhghhhhhhd..',
      '....ffffffffffggggggggggghhhhhh..',
      '...fffffffffffhhggggggggghhhhhhc.',
      '..dfffffffffffhggggggggghhhhhhhf.',
      '.bffffffffffffhhhgggggggghghhhhhb',
      '.dffffffffffffhhhghggggggggghhhhb',
      '.fffffffffffffghgghggggggggghhhhb',
      '.fffffffffffffgggghggggggggghhhhb',
      '.ffffffffffffffggggggggggggghhhhb',
      '.ffffffffffffffgghghggggggghhhhhb',
      'lffffffffffffffgghghgggggggghhhhb',
      'lffffffffffffffgghggggggggggghhhb',
      'lffffffffffffffggghgggghhhhhhhhhb',
      'offfffffdccfffgghghggggggggggghhb',
      '.fffffffpggqffhghggggggghhhhgghhb',
      '.dffffdrssggtshhgggggggghhhhggghl',
      '..ffffpstqqgssgggggggghhhhhhhghh.',
      '..cfffustvttgggggggggggggggggggf.',
      '...fffusvvqqggggggghhhgggggggggc.',
      '....dfuvstqqhggggggghhhggggggghc.',
      '.....ffBgttqhhgggggghhgggggghhhc.',
      '.....bfbphvqfggggggggggghhhhgghc.',
      '......cfptggggggggggggggggggghhh.',
      '.......DfpEFFfggggggggggggggghhhh',
      '.......oFffbpffgggggggggggggghhhf',
      '........oFffFFfggggggggggggghhhf.',
      '.........bffFGfgggggggggggggghhb.',
      '..........tvvDffgggggggggggggDD..',
      '..........vvvFGffhggggggghhgggd..',
      '..........FFvvDDffgggggghgghhh...',
      '..........FvvvFbpDfggggghhghgb...',
      '..........vvvvFcDbEEbFggghhhgl...',
      '..........vvvvfFbbbbE....obcE....',
      '..........vvvvsfDcbb.............',
      '..........vvvvggFDcb.............',
      '..........FvvggggfF..............',
      '.........bFFvgghhgg..............',
      '.........Fvvfgggghh..............',
    ],
  },
  back3: {
    x: -16,
    y: -18,
    palette: [
      '#6e655b',
      '#58514b',
      '#7b5e53',
      '#9f7d68',
      '#c1987d',
      '#d5a786',
      '#ebbf97',
      '#e0b390',
      '#91715f',
      '#f2d1a9',
      '#aa866f',
      '#615b53',
      '#654943',
      '#d48d69',
      '#edac80',
      '#bf8062',
      '#e1895c',
      '#b37256',
      '#e79c6f',
      '#a6614d',
      '#56423e',
      '#744f4a',
      '#d69b78',
    ],
    rows: [
      '..........bbbcc......bb..........',
      '..........ddeffggggggee..........',
      '.........ffffhggggggggggg........',
      '.......ifffffggggggggggggj.......',
      '......kfffffhggggggggggggjgb.....',
      '.....dffffffggggggggggggggggc....',
      '....cfffffffgggggggggggggggjj....',
      '....ffffffffgggggggggggggggggj...',
      '...ffffffffhggggggggggggggggjj...',
      '..cffffffffggggggggggggggggggje..',
      '.bkffffffffggggggggggggggggggge..',
      '.ceffffffffgggggggggggggggggggj..',
      '.ifffffffffgggggggggggggggggggji.',
      '.ifffffffffgggggggggggggggggggji.',
      '.ifffffffffgggggggggggggggggggji.',
      '.ifffffffffgggggggggggggggggggj..',
      '.ifffffffffgggggggggggggggggggj..',
      '.ifffffffffgggggggggggggggggggj..',
      '.ifffffffffgggggggggggggggggggj..',
      '.meffffffffggggggggggggggggggggc.',
      '.meffffffffggggggggggggggggggggc.',
      'hneffffffffggggggggggggggggggghog',
      'onpffffffffgggggggggggggggggggooj',
      'oqrffffffffhggggggggggggggggggnog',
      'osreffffffffgggggggggggggggggjrog',
      'ponrffffffffggggggggggggggggghngi',
      'bpntefffffffgggggggggggggggjjrsp.',
      '..npkfffffffhggggggggggggggggts..',
      '....cfffffffhgggggggggggggggf....',
      '....cefffffffggggggggggggggg.....',
      '.....cfffffffhgggggggggggggf.....',
      '......kfffffffgggggggggggggm.....',
      '......mfffffffhgggggggggggc......',
      '........fffffffgggggggggg........',
      '.........fffffffggggggggm........',
      '.........ceffffffggggghmb........',
      '..........mrefffffgghec..........',
      '...........vrdddfffeet...........',
      '............rrdfffffe............',
      '............rpwfhggff............',
      '............qnfhggggh............',
      '............sswgggggj............',
      '............wwwgggggj............',
      '............wwwgggggj............',
    ],
  },
  back: {
    x: -18,
    y: -18,
    palette: [
      '#6e655c',
      '#5a524e',
      '#927767',
      '#a5836e',
      '#b68f75',
      '#d7ac8b',
      '#eabe98',
      '#c59b7d',
      '#f4d4ab',
      '#6f5651',
      '#b27962',
      '#7a4f46',
      '#f3b383',
      '#9e6a5a',
      '#e5a980',
      '#e69e71',
      '#c97555',
      '#e39063',
      '#c78e6b',
      '#685240',
      '#d38562',
      '#905d4b',
      '#3b2a2a',
      '#73453c',
      '#59473e',
      '#b46c53',
    ],
    rows: [
      '............bb...cc..cc............',
      '..........bbdeffgggggggee..........',
      '..........hffffgggggggggggf........',
      '.........ffffffggggggggggggi.......',
      '........ffffffggggggggggggggib.....',
      '......cffffffggggggggggggggggi.....',
      '......fffffffgggggggggggggggggi....',
      '.....ffffffffggggggggggggggggggg...',
      '.....ffffffffggggggggggggggggggi...',
      '....hffffffffggggggggggggggggggid..',
      '....fffffffffgggggggggggggggggggd..',
      '...ffffffffffgggggggggggggggggggg..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggi..',
      '...ffffffffffgggggggggggggggggggg..',
      '...kfffffffffgggggggggggggggggggg..',
      '..clfffffffffgggggggggggggggggggfh.',
      'bhmnfffffffffgggggggggggggggggggogi',
      'bhpqhffffffffgggggggggggggggggggpoi',
      'bhprkffffffffgggggggggggggggggggroi',
      '.sppqffffffffgggggggggggggggggggrmi',
      '.lppqhffffffffgggggggggggggggggsrot',
      '..tpueffffffffgggggggggggggggggkon.',
      '..bvrvffffffffgggggggggggggggghku..',
      '......ffffffffggggggggggggggggx....',
      '......nfffffffgggggggggggggggg.....',
      '.......ffffffffgggggggggggggg......',
      '.......vfffffffgggggggggggggh......',
      '........dfffffffgggggggggggf.......',
      '........bkfffffffgggggggggft.......',
      '..........sffffffggggggggg.........',
      '...........sffffffggggggf..........',
      '............ksfffffgggfh...........',
      '.............qkkkffhhhu............',
      '.............vqkeffffhu............',
      '............bvqrffggfhs............',
      '............bzrpfgggggo............',
      '............bsppfgggggg............',
      '............bsppfgggggg............',
      '............vpppogggggg............',
    ],
  },
};

export const REF_FACE: Partial<Record<View, FineFace>> = {
  front: {
    eyes: {
      x: -12,
      y: 4,
      palette: [
        '#987663',
        '#ab8771',
        '#c59d81',
        '#bd9274',
        '#2f1c16',
        '#3a2720',
        '#624536',
        '#4b342c',
        '#201311',
        '#4e3f3e',
        '#938987',
        '#a69f9e',
        '#d4bfb2',
        '#efe9e6',
        '#f5f3f2',
        '#e0cab6',
        '#eddfd4',
        '#774830',
        '#f6dbbc',
        '#8c644d',
        '#e9e3df',
      ],
      rows: [
        '..aaabd...........bbbb..',
        'chiiiiihd.......dfiiiefa',
        'djkuiilkg.......hleeekke',
        '.mnuueuom.......poeefnn.',
        '..nuueheq.......qhfefnn.',
        '..qlgrgh........shrrho..',
        '....hhgc.........bggt...',
      ],
    },
    closed: {
      x: -11,
      y: 7,
      palette: ['#201311'],
      rows: ['aaaaaaa.........aaaaaa'],
    },
    brows: {
      x: -11,
      y: 0,
      palette: [
        '#8a6d5e',
        '#987663',
        '#c59d81',
        '#bd9274',
        '#2f1c16',
        '#3a2720',
        '#6b503e',
        '#624536',
        '#775947',
      ],
      rows: ['deeeeefa.......geeefff.', 'bgggghhi.......iiggiiac'],
    },
    nose: {
      x: -1,
      y: 11,
      palette: ['#bd9274', '#d98861', '#eba376', '#d89c71'],
      rows: ['bc', 'ad'],
    },
    mouth: {
      x: -1,
      y: 15,
      palette: ['#bd9274', '#d98861', '#b8775e'],
      rows: ['cba'],
    },
  },
  front3: {
    eyes: {
      x: -5,
      y: 4,
      palette: [
        '#5c544d',
        '#876b5b',
        '#e2b691',
        '#bc9f83',
        '#b48b71',
        '#2e1d15',
        '#422c22',
        '#c99579',
        '#7d5f4c',
        '#674d3d',
        '#1e1210',
        '#a9a29f',
        '#a2938d',
        '#eee6e1',
        '#e9dacf',
        '#e0c3ac',
        '#beaca2',
        '#5f3a29',
        '#f5f3f1',
        '#bfb7b1',
        '#7e4e35',
        '#e9e3df',
      ],
      rows: [
        '.eeeedc.......c.ee.',
        'ffffkfic.....cvkkkb',
        'vgfflmmc.....bvvfm.',
        'nafvvno......plffnq',
        'narfvvn......egffse',
        '.tuuut.......prugs.',
        '.cjjhc.....cc.brh..',
      ],
    },
    closed: {
      x: -4,
      y: 7,
      palette: ['#1e1210'],
      rows: ['aaaaaa.....aaaaaa'],
    },
    brows: {
      x: -5,
      y: 0,
      palette: ['#876b5b', '#2e1d15', '#422c22', '#c99579', '#7d5f4c', '#725644', '#674d3d'],
      rows: ['abbbbbc.......abbbc', 'aeffffed......ffgga'],
    },
    nose: {
      x: 6,
      y: 11,
      palette: ['#c99579', '#e1a076', '#d48f6a'],
      rows: ['bba', 'ca.'],
    },
    mouth: {
      x: 4,
      y: 15,
      palette: ['#e2b691', '#c58567'],
      rows: ['bbb', 'aaa'],
    },
  },
  side: {
    eyes: {
      x: 6,
      y: 3,
      palette: [
        '#a6816d',
        '#b79677',
        '#d6ab87',
        '#8f6e5f',
        '#312018',
        '#422d22',
        '#6a443a',
        '#e3a077',
        '#4e362b',
        '#201412',
        '#ada4a2',
        '#ebe2db',
        '#7a4b32',
        '#e9e3df',
      ],
      rows: ['..baac', 'ciejjg', 'dneeka', '.lfen.', '.lfif.', '..fmi.', '...gh.'],
    },
    closed: {
      x: 7,
      y: 6,
      palette: ['#201412'],
      rows: ['aaaa'],
    },
    brows: {
      x: 6,
      y: -1,
      palette: ['#a6816d', '#d6ab87', '#8f6e5f', '#312018', '#422d22', '#77574d', '#e9e3df'],
      rows: ['.cdddde', 'bagfffg'],
    },
  },
};
